// @flow
import React from 'react';
import gql from 'graphql-tag';
import {compose} from 'redux';
import {connect} from 'react-redux';
import HitInfoMobile from './HitInfoMobile';
import HitInfoDesktop from './HitInfoDesktop';
import {media} from '../../utils/breakpoints';
import {injectIntl} from 'react-intl';
import {
    HA_SERP_MOBILE_HIT_HEADLINE,
    HA_SERP_FREE_CANCELLATION,
    HA_URGENCY_HITCOUNT_MESSAGE_SERVICE_SCARCITY
} from '../../abacus/experiments';
import {fragments as geoDistanceFragments} from '../GeoDistance/GeoDistance';
import {fragments as priceFragments} from '../Price/Price';
import {fragments as detailsFragments} from '../Details/Details';
import {fragments as ratingFragments} from '../Rating/Rating';
import {fragments as hitSuperlativeFragments} from '../HitSuperlative/HitSuperlative';

import type {IntlShape} from 'react-intl';
import type {Shape as DetailsShape} from '../Details/Details';
import type {Shape as GeoDistanceShape} from '../GeoDistance/GeoDistance';
import type {Shape as PriceShape, PriceSummaryShape} from '../Price/Price';
import type {Shape as RatingShape} from '../Rating/Rating';
import type {Shape as HitSuperlativeShape} from '../HitSuperlative/HitSuperlative';
import type {State as ReduxState} from '../../reducers/index';

import './HitInfo.less';

export const fragments = {
    SERPHit: gql`
        fragment HitInfoSERPHit on SERPHit {
            ...DetailsSERPHit
            ...PriceSERPHit
            ...GeoDistanceSERPHit
            ...RatingSERPHit
            ...HitSuperlativeSERPHit
            badges
            detailPageUrl
            freeCancellation
            headline
            instantBookable
            listingId
            minStay
            propertyId
            propertyType
            recentlyViewed
            numRecentViews
            urgencyMessage {
                icon
                message
            }
        }
        ${detailsFragments.SERPHit}
        ${geoDistanceFragments.SERPHit}
        ${priceFragments.SERPHit}
        ${ratingFragments.SERPHit}
        ${hitSuperlativeFragments.SERPHit}
    `
};

type HitBadge = 'PP_ACCEPTED';

type HitInfoPropertyIdShape = {
    badges: ?(HitBadge[]),
    instantBookable: boolean,
    minStay: string,
    propertyType: string
};

type HitInfoHeadlineShape = {
    headline: string,
    detailPageUrl: string
};

export type Shape = {
    listingId: string,
    price: PriceShape,
    priceSummary: ?PriceSummaryShape,
    details: DetailsShape,
    freeCancellation?: boolean,
    superlative?: HitSuperlativeShape,
    recentlyViewed?: boolean,
    numRecentViews?: number,
    popular: boolean,
    urgencyMessage?: {
        icon: string,
        message: string
    },
    locale: string
} & RatingShape &
    GeoDistanceShape &
    HitInfoPropertyIdShape &
    HitInfoHeadlineShape;

type StateProps = {
    isHovered: boolean,
    isMetric: boolean,
    freeCancellationVariant?: number,
    hideSuperlative: boolean,
    brand: string,
    showDesktopHits: boolean,
    showMobileHitHeadlines: boolean,
    useUrgencyMessage: boolean
};

type IntlProps = {
    intl: IntlShape
};

export type OwnProps = {
    amenitiesBadges?: {name: string}[],
    isDatelessSearch?: boolean
} & Shape &
    IntlProps;

export type Props = OwnProps & StateProps;

export function HitInfo(props: Props) {
    const {
        averageRating,
        badges,
        brand,
        geoDistance,
        intl: {formatMessage},
        reviewCount,
        showDesktopHits,
        showMobileHitHeadlines
    } = props;

    const showPartnerBadge = !!badges && badges.indexOf('PP_ACCEPTED') !== -1;

    const instantBookableTitle = formatMessage({
        id: 'instantConfirmationSecondaryLabel',
        defaultMessage: 'Book without waiting for owner approval'
    });

    const premierPartnerDescription = formatMessage(
        {
            id: 'premierPartnerDescription',
            defaultMessage:
                'This partner has demonstrated and is committed to fast response times, the best rates, and a great guest experience.'
        },
        {
            brand
        }
    );

    const propsToPass = {
        ...props,
        showPartnerBadge,
        instantBookableTitle,
        premierPartnerDescription,
        // Without these bottom 3 lines, Flowtype is not happy
        averageRating,
        reviewCount,
        geoDistance
    };

    if (showDesktopHits) {
        return <HitInfoDesktop {...propsToPass} />;
    }
    return (
        <HitInfoMobile
            {...{
                showMobileHitHeadlines,
                ...propsToPass
            }}
        />
    );
}

const mapStateToProps = (
    {
        map: {listingId: hoveredListingId},
        system: {brand, isDesktop, isMetric, locale},
        viewport: {breakpoint},
        abacus
    }: ReduxState,
    {listingId, isDatelessSearch}: OwnProps
) => ({
    freeCancellationVariant: isDatelessSearch
        ? 0
        : (abacus[HA_SERP_FREE_CANCELLATION] || {}).value || 0,
    isHovered: listingId === hoveredListingId && isDesktop,
    isMetric,
    showMobileHitHeadlines:
        (abacus[HA_SERP_MOBILE_HIT_HEADLINE] || {}).value === 1 || false,
    brand,
    showDesktopHits: media(breakpoint, {
        minWidth: 'SM_MIN'
    }),
    hideSuperlative:
        isDatelessSearch ||
        (abacus[HA_SERP_FREE_CANCELLATION] &&
            abacus[HA_SERP_FREE_CANCELLATION].value !== 3) ||
        false,
    useUrgencyMessage:
        abacus[HA_URGENCY_HITCOUNT_MESSAGE_SERVICE_SCARCITY] &&
        abacus[HA_URGENCY_HITCOUNT_MESSAGE_SERVICE_SCARCITY].value !== 0,
    locale
});

export default compose(
    injectIntl,
    connect(mapStateToProps)
)(HitInfo);
